/**
 * Plan types — enums and interfaces for Smart Plans.
 *
 * DbPack (pack template) and DbPlanInstance (running plan) share
 * the `plan` Dexie table, discriminated by `kind`.
 */

// ---------------------------------------------------------------------------
// Enums
// ---------------------------------------------------------------------------

export enum AnchorDateType {
  Deadline = "deadline",
  Event = "event",
  Start = "start",
  Rolling = "rolling",
}

export enum PlanInstanceStatus {
  Active = 0,
  Completed = 1,
  Cancelled = -1,
  Paused = -2,
}

export enum AutomationTrigger {
  AnchorDateChanged = "anchor_date_changed",
  StepCompleted = "step_completed",
  StepOverdue = "step_overdue",
  AllStepsCompleted = "all_steps_completed",
  DaysBeforeAnchor = "days_before_anchor",
}

// ---------------------------------------------------------------------------
// Pack Step — template-level step definition
// ---------------------------------------------------------------------------

export interface PackStep {
  stepKey: string
  titleTemplate: string
  noteTemplate?: string
  offsetRule: string
  sequenceOrder: number
  relationType?: "prerequisite" | "parallel"
  condition?: string
  optional?: boolean
  tags?: string[]
  checklistItems?: string[]
  estimatedMinutes?: number
  reminderOffset?: string
  resources?: PackResource[]
}

export interface PackResource {
  title: string
  url: string
  isAffiliate?: boolean
  disclosure?: string
  condition?: string
}

export interface PackQuestion {
  key: string
  label: string
  type: "text" | "select" | "boolean" | "number"
  options?: { value: string; label: string }[]
  default?: unknown
  required?: boolean
  condition?: string
}

export interface PackAutomation {
  trigger: AutomationTrigger
  action: AutomationAction
  condition?: string
}

export interface AutomationAction {
  type: "reschedule_all" | "notify" | "create_task" | "complete_plan" | "suggest_review"
  params?: Record<string, unknown>
}

// ---------------------------------------------------------------------------
// DbPack — reusable pack template (stored in `plan` table with kind="pack")
// ---------------------------------------------------------------------------

export interface DbPack {
  id: string
  kind: "pack"
  name: string
  description?: string
  icon?: string
  category: string
  triggers?: string[]
  anchorDateType: AnchorDateType
  questions?: PackQuestion[]
  steps: PackStep[]
  automations?: PackAutomation[]
  locale?: string
  version: number
  isBuiltIn?: boolean
  isPublished?: boolean
  creatorId?: string
  owner?: string
  realmId?: string
}

// ---------------------------------------------------------------------------
// PlanStepInstance — per-task metadata in a running plan
// ---------------------------------------------------------------------------

export interface PlanStepInstance {
  taskId: string
  stepKey: string
  offsetRule: string
  sequenceOrder: number
  estimatedMinutes?: number
  reminderOffset?: string
}

// ---------------------------------------------------------------------------
// DbPlanInstance — running plan (stored in `plan` table with kind="instance")
// ---------------------------------------------------------------------------

export interface DbPlanInstance {
  id: string
  kind: "instance"
  anchorTaskId: string
  packId?: string
  packVersion?: number
  anchorDate: string
  anchorDateType: AnchorDateType
  status: PlanInstanceStatus
  answers?: Record<string, unknown>
  steps: PlanStepInstance[]
  /** Original anchor task title before plan conversion (for undo) */
  originalTitle?: string
  /**
   * The anchor task's own schedule before the plan scheduled it at the anchor date, so deleting the
   * plan can put it back: the startAtIndex values, or null when it had none. Undefined on plans
   * made before this field existed (deleting those leaves the schedule as it is).
   */
  originalSchedule?: { startAt: string; type: number; subType: number } | null
  createdAt: string
  createdBy: string
  updatedAt?: string
  updatedBy?: string
  isDeleted?: 0 | 1
  deletedAt?: string
  owner?: string
  realmId?: string
}

// ---------------------------------------------------------------------------
// DbSkill — reusable AI plan skill (stored in `plan` table with kind="skill")
//
// A Skill is domain knowledge that guides AI plan generation.
// Unlike a Pack (hardcoded steps), a Skill provides context, constraints,
// and example steps that an LLM uses to dynamically generate a plan
// tailored to the user's situation.
// ---------------------------------------------------------------------------

export interface SkillConstraints {
  minSteps?: number
  maxSteps?: number
  anchorDateType?: AnchorDateType
  requiredCategories?: string[]
  allowedOffsetRange?: { minDays?: number; maxDays?: number }
}

export interface SkillExampleStep {
  title: string
  typicalOffset: string
  category?: string
  estimatedMinutes?: number
}

export interface DbSkill {
  id: string
  kind: "skill"
  name: string
  description?: string
  icon?: string
  category: string
  /** System prompt that guides the AI when generating plans with this skill. */
  systemPrompt: string
  /** Structural constraints for generated plans. */
  constraints: SkillConstraints
  /** Few-shot examples the AI can reference. */
  exampleSteps: SkillExampleStep[]
  /** Legacy questions (optional, for hybrid skill+pack usage). */
  questions?: PackQuestion[]
  /** Pack that was used as seed for this skill (if migrated). */
  sourcePackId?: string
  locale?: string
  version: number
  isBuiltIn?: boolean
  isPublished?: boolean
  creatorId?: string
  owner?: string
  realmId?: string
}

// ---------------------------------------------------------------------------
// AI-generated plan proposal (transient — not stored until accepted)
// ---------------------------------------------------------------------------

export interface AiPlanStep {
  title: string
  note?: string
  offsetRule: string
  sequenceOrder: number
  tags?: string[]
  checklistItems?: string[]
  estimatedMinutes?: number
  reminderOffset?: string
  category?: string
}

export interface AiPlanProposal {
  /** Short name for the plan. */
  name: string
  /** AI's reasoning for this plan structure. */
  reasoning?: string
  /** Anchor date type for the generated plan. */
  anchorDateType: AnchorDateType
  /** Proposed steps. */
  steps: AiPlanStep[]
  /** Suggested questions if the user should provide more context. */
  suggestedQuestions?: string[]
}

// ---------------------------------------------------------------------------
// AI chat messages
// ---------------------------------------------------------------------------

export interface AiChatMessage {
  role: "user" | "assistant" | "system"
  content: string
  /** Attached proposal (assistant messages may include structured plan data). */
  proposal?: AiPlanProposal
  timestamp?: string
}

// ---------------------------------------------------------------------------
// Discriminated union for the `plan` table
// ---------------------------------------------------------------------------

export type DbPlanEntry = DbPack | DbPlanInstance | DbSkill
